import Link from 'next/link'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import type {Block} from '@/services/kontent-ai/types'
import {KontentResponsivePicture} from '@/products/flexible-web-toolkit/components/responsive-picture'
import {getContainerClass} from '../helpers'

enum ImageWidth {
	Small = 'w-20',
	Medium = 'w-40',
	Large = 'w-80',
	Full = 'w-full',
}

// Complete, static class strings per spacing choice (mirrors
// `spacing` in styles.ts but swaps margin for padding-top on mobile).
// Tailwind's compiler only discovers classes it can see literally in source,
// so these must stay fully written out rather than built via string
// concatenation/replace - otherwise variants like `sm:my-6` are silently
// dropped since the safelist only covers `md` variants.
const MOBILE_SAFE_SPACING: Record<string, string> = {
	default: 'pt-5 sm:pt-0 my-0 sm:my-6 md:my-8',
	top: 'pt-5 sm:pt-0 mt-0 sm:mt-6 md:mt-8',
	bottom: 'pt-5 sm:pt-0 mb-6 md:mb-8',
	none: 'my-0 py-0',
}

export type ImageContentItem = IContentItem<{
	image: Elements.AssetsElement
	mobileImage: Elements.AssetsElement
	imageWidth: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	imageLink: Elements.TextElement
	imageAlignment?: Elements.MultipleChoiceElement
	openInNewTab: Elements.MultipleChoiceElement
	removeHyperlink?: Elements.MultipleChoiceElement
	effectCls?: Elements.TextElement
}>

export const ImageBlock: Block<ImageContentItem> = ({block}) => {
	// The mobile-safe override is used to remove the top & bottom margin on mobile (without editing the highly reused spacing const)

	const spacingChoice =
		MOBILE_SAFE_SPACING[
			block.elements.snippetSpacingSpacing[0]?.codename || 'default'
		] ?? MOBILE_SAFE_SPACING.none
	const imageWidth = block.elements.imageWidth[0]?.codename

	const sizes: Record<string, string> = {
		small: ImageWidth.Small,
		medium: ImageWidth.Medium,
		large: ImageWidth.Large,
		full: ImageWidth.Full,
	}

	const imgSize =
		sizes[imageWidth as keyof typeof imageWidth] || ImageWidth.Medium

	const imageSizesPxMap: Record<ImageWidth, string> = {
		[ImageWidth.Small]: '80px',
		[ImageWidth.Medium]: '160px',
		[ImageWidth.Large]: '320px',
		[ImageWidth.Full]: '100vw',
	}
	const sizesHint = imageSizesPxMap[imgSize as ImageWidth]

	const containerSize = getContainerClass(
		block.elements.snippetContainerResize[0]?.codename ?? '',
		'container-narrow'
	)

	const getImageAlignment = (alignment: string | undefined): string => {
		switch (alignment) {
			case 'left':
				return 'justify-start'
			case 'center':
				return 'justify-center'
			case 'right':
				return 'justify-end'
			default:
				return 'justify-start'
		}
	}

	const justifyClass = getImageAlignment(
		(
			block.elements.imageAlignment as {codename?: string}[] | undefined
		)?.[0]?.codename
	)

	const mobileImageEl = block.elements.mobileImage[0] ?? null
	const desktopImage = block.elements.image[0] ?? mobileImageEl

	const imageClassName = clsx(
		imgSize,
		'h-auto',
		block.elements.effectCls as string
	)
	const imagePictureClassName = clsx('block', imgSize)

	return (
		<div
			className={clsx(
				justifyClass,
				'flex',
				'rounded-md',
				'sm:px-8',
				'sm:py-4',
				containerSize,
				spacingChoice
			)}
			data-kontent-element-codename="card_image"
		>
			{desktopImage ? (
				<>
					{block.elements.imageLink &&
					!block.elements.removeHyperlink ? (
						<Link
							className={clsx('block h-full', imgSize)}
							href={block.elements.imageLink || ''}
							rel="noreferrer"
							target={
								block.elements.openInNewTab[0]?.codename ===
								'yes'
									? '_blank'
									: '_self'
							}
						>
							<KontentResponsivePicture
								className={imageClassName}
								desktopAsset={desktopImage}
								mobileAsset={mobileImageEl}
								pictureClassName={imagePictureClassName}
								sizes={sizesHint}
							/>
						</Link>
					) : (
						<KontentResponsivePicture
							className={imageClassName}
							desktopAsset={desktopImage}
							mobileAsset={mobileImageEl}
							pictureClassName={imagePictureClassName}
							sizes={sizesHint}
						/>
					)}
				</>
			) : null}
		</div>
	)
}
